import {useSearchParamStructured} from '@raison/navigation';
import {RsnAmount} from '@raison/ui-kit';
import {type} from 'arktype';
import React, {useState} from 'react';
import {Navigate, NavigateOptions, useNavigate} from 'react-router';
import {AccountId, MethodId, MethodType} from '~/entities/operations-fiat';
import {executeOperationFinishNavigation} from '~/entities/transactions-history';
import {TransactionId, TransactionIdArkType} from '~/library/types';
import {CardTopupOrderInfoPage} from '~/processes/fiat-operations/ui/Topup/CardTopupOrderInfoPage';
import {OperableAccount, OperableTopupMethod, OperationModel} from '~/redux/finances/domain';
import {useNavigateToOperation} from '../../lib/useNavigateToOperation';
import {TopupIntro} from '../../ui/Topup/TopupIntro';
import {TopupRequisites} from './TopupRequisites';

export const TopupParams = type({
    accountId: AccountId,
    methodType: MethodType.optional(),
    methodId: MethodId.optional(),
});
export type TopupParams = typeof TopupParams.infer;

export type TopupPageProps = {
    providerName: string;
    accounts: OperableAccount[];
    methods: OperableTopupMethod[];
    account: OperableAccount;
    method: OperableTopupMethod | undefined;
    operations: OperationModel[];
};

const parseParam = type.never
    .or({
        step: 'undefined?',
    })
    .or({
        step: '"requisites"',
    })
    .or({
        step: '"finish"',
        orderId: TransactionIdArkType,
    });
const decodeParam = parseParam.assert.bind(parseParam);

export type Params = typeof parseParam.infer;

export function TopupPage({methods, accounts, account, method, providerName, operations}: TopupPageProps) {
    const navigate = useNavigate();
    const {navigateToOperation, createOperationPath} = useNavigateToOperation('topup', operations);

    const [introCompleted, setIntroCompleted] = useState(false);

    const [direction, setDirection] = useSearchParamStructured('topup', {
        decode: decodeParam,
        fallback: {},
    });

    const [amount, setAmount] = useState(RsnAmount.empty);

    if (!introCompleted && direction.step) {
        return <Navigate to={createOperationPath(account, method || null)} replace />;
    }

    if (direction?.step === 'requisites' && method?.type === 'payment_network') {
        return <TopupRequisites account={account} method={method} amount={amount} />;
    }

    if (direction.step === 'finish' && method?.type === 'card') {
        return (
            <CardTopupOrderInfoPage
                currency={{code: account.currencyCode, decimals: account.currencyDecimals}}
                amount={RsnAmount.toString(amount)}
                goToDetails={() => executeOperationFinishNavigation(direction.orderId as TransactionId, navigate)}
                direction={'in'}
                cardId={method.id}
                isCardVerified={!method.flags.some(f => f.reasonCode === 'CARD_UNVERIFIED')}
            />
        );
    }

    const handleChangeStep = (amount: RsnAmount, params: Params, options?: NavigateOptions) => {
        setAmount(amount);
        setIntroCompleted(true);
        setDirection(params, options);
    };

    return (
        <TopupIntro
            providerName={providerName}
            accounts={accounts}
            methods={methods}
            account={account}
            method={method}
            onOperationChange={navigateToOperation}
            onStepChange={handleChangeStep}
            initialAmount={amount}
        />
    );
}
